import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
    Typography, 
    Space, 
    Tag, 
    Button, 
    message, 
    Result,
    Upload,
    Spin,
    Tooltip
} from 'antd';
import { 
    FileOutlined,
    FolderOpenOutlined,
    EyeOutlined,
    UploadOutlined,
    DeleteOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    QuestionCircleOutlined,
    MailOutlined,
    RollbackOutlined,
    CommentOutlined
} from '@ant-design/icons';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import AccessCodeModal from '../common/AccessCodeModal';
import '../../styles/documents/DocumentView.css';
import '../../styles/documents/DocumentRequestRecords.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useBecomeGuest } from '../../contexts/BecomeGuestContext';
import { isConvertible } from '../../utility/fileTypes';

const { Title, Text } = Typography;

const DocumentView = ({ mailboxHref }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { setCanBecomeGuest } = useBecomeGuest();
    const { unique_identifier } = useParams();
    const [documentData, setDocumentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [requiresAccessCode, setRequiresAccessCode] = useState(false);
    const [viewerEmail, setViewerEmail] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [removedFiles, setRemovedFiles] = useState(new Set());
    const [systemSettings, setSystemSettings] = useState(null);
    const [attachmentPreviewDoc, setAttachmentPreviewDoc] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    
    // Resizable columns state
    const [columnWidths, setColumnWidths] = useState(() => {
        const saved = localStorage.getItem('docview_column_widths');
        return saved ? JSON.parse(saved) : { body: 60, files: 40 };
    });
    const [isResizing, setIsResizing] = useState(false);
    const contentRowRef = useRef(null);
    const allSentOnLoadRef = useRef(null);

    const sortedDocumentFiles = useMemo(() =>
        [...(documentData?.document_files || [])].sort((a, b) => (a.document_name || a.title || '').localeCompare(b.document_name || b.title || '')),
        [documentData?.document_files]
    );

    const fetchDocument = async () => {
        try {
            setLoading(true);
            const headers = {};
            
            // Check if we should preserve the view count
            let shouldPreserveViews = false;
            
            // First check if preserveViews is explicitly set in location state
            if (location.state?.preserveViews === true) {
                shouldPreserveViews = true;
                // Clear the state after using it to prevent preserving views on future navigations
                window.history.replaceState({}, document.title);
            } else if (location.state?.preserveViews === false) {
                // If preserveViews is explicitly set to false, we should NOT preserve views
                // This takes precedence over the session storage check
                shouldPreserveViews = false;
                // Clear the state after using it
                window.history.replaceState({}, document.title);
            } else {
                // Case 2: No explicit state - check if we've already viewed this document in this session
                const viewedDocumentsStr = sessionStorage.getItem('viewedDocuments') || '[]';
                const viewedDocuments = JSON.parse(viewedDocumentsStr);
                if (viewedDocuments.includes(unique_identifier)) {
                    shouldPreserveViews = true;
                } else {
                    // Add this document to the list of viewed documents in this session
                    viewedDocuments.push(unique_identifier);
                    sessionStorage.setItem('viewedDocuments', JSON.stringify(viewedDocuments));
                }
            }
            
            // Set header if we should preserve views
            if (shouldPreserveViews) {
                headers['X-Preserve-Views'] = 'true';
            }
            
            let endpoint = `/api/document/${unique_identifier}/`;
            const path = window.location.pathname;
            
            // Check if this is a reply view
            if (path.includes('/reply/')) {
                endpoint = `/api/document/reply/${unique_identifier}/`;
                
                // Check if this is a reply-all request
                if (window.location.search.includes('reply_all=true')) {
                    endpoint += '?reply_all=true';
                }
            }
            
            const response = await axios.get(endpoint, {
                headers
            });
            
            const messageData = response.data.message;
            setDocumentData(messageData);
            setViewerEmail(response.data.viewer_email);
            setError(null);
            setRequiresAccessCode(false);
            const files = messageData?.document_files || [];
            if (files.length > 0 && allSentOnLoadRef.current === null) {
                allSentOnLoadRef.current = files.every(f => f.has_been_read);
            }
        } catch (err) {
            if (err.response?.status === 401 && err.response?.data?.requires_verification) {
                setRequiresAccessCode(true);
                setError(null);
            } else if (err.response?.status === 410) {
                setError(t('documents.view.errorExpired'));
            } else if (err.response?.status === 404) {
                setError(t('documents.view.errorNotFound'));
            } else {
                setError(t('documents.view.errorFailedToLoad'));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        allSentOnLoadRef.current = null;
        fetchDocument();
        fetchSystemSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unique_identifier]);

    const handleAccessCodeSuccess = () => {
        setRequiresAccessCode(false);
        setCanBecomeGuest?.(true);
        fetchDocument();
    };

    // Handle column resize
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !contentRowRef.current) return;
            
            const containerRect = contentRowRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            
            // Constrain body width between 30% and 80%
            let bodyWidth = Math.max(30, Math.min(80, mousePercent));
            const filesWidth = 100 - bodyWidth;
            
            const newWidths = { body: bodyWidth, files: filesWidth };
            setColumnWidths(newWidths);
            localStorage.setItem('docview_column_widths', JSON.stringify(newWidths));
        };

        const handleMouseUp = () => {
            if (isResizing) {
                setIsResizing(false);
                document.body.classList.remove('docview-resizing');
            }
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('docview-resizing');
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('docview-resizing');
        };
    }, [isResizing]);

    const fetchSystemSettings = async () => {
        try {
            const response = await axios.get('/api/system-settings/');
            setSystemSettings({
                allowed_file_types: response.data.allowedFileTypes,
                max_upload_size: response.data.maxUploadSize
            });
        } catch (err) {
            console.error('Failed to fetch system settings:', err);
        }
    };

    const handleDownloadAll = async () => {
        try {
            const response = await axios.get(`/api/documents/${documentData.unique_identifier}/download-all/`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'documents.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error(t('documentPreview.errors.downloadFailed'));
        }
    };

    const handleRemoveFile = (documentFileUniqueIdentifier) => {
        try {
            // Always remove from local uploaded files if it exists
            if (uploadedFiles[documentFileUniqueIdentifier]) {
                setUploadedFiles(prev => {
                    const newFiles = { ...prev };
                    delete newFiles[documentFileUniqueIdentifier];
                    return newFiles;
                });
            }
            
            // Always mark as removed (this handles both local uploads and backend files)
            // This ensures that even if there was a backend file, it won't show after removing a local upload
            setRemovedFiles(prev => new Set([...prev, documentFileUniqueIdentifier]));
            
            message.success(t('documents.view.fileRemovedSuccessfully'));
        } catch (err) {
            message.error(t('documents.view.errorFailedToRemoveFile'));
        }
    };

    const handleDocumentDownload = async (documentFileId, fileName) => {
        try {
            const response = await axios.get(`/api/documents/download/${documentFileId}/`, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName || 'document');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
            message.success(t('common.downloaded') || t('common.download'));
        } catch (err) {
            message.error(t('documentPreview.errors.downloadFailed') || 'Download failed');
        }
    };

    const handleSendDocuments = async () => {
        try {
            // Check if there are any files selected
            const selectedFiles = Object.entries(uploadedFiles);
            
            if (selectedFiles.length === 0) {
                message.warning(t('documents.view.noFilesToSend'));
                return;
            }
            
            // Check if we have document data with the actual document unique_identifier
            if (!documentData || !documentData.unique_identifier) {
                message.error(t('documents.view.errorDocumentNotLoaded'));
                return;
            }
            
            // Create FormData to send files
            const formData = new FormData();
            
            // Add each selected file to the FormData
            selectedFiles.forEach(([documentFileId, fileData]) => {
                formData.append(`file_${documentFileId}`, fileData.file);
            });
            
            // Do NOT set Content-Type manually - browser/axios must set it with boundary for multipart
            await axios.post(`/api/save_requested_documents/${documentData.unique_identifier}/`, formData);
            
            message.success(t('documents.view.documentsSent'));
            
            // Clear uploaded files after successful submission
            setUploadedFiles({});
            
            // Navigate to mailbox - profile users go to /profile/mailbox, free account and unauthenticated go to /mailbox
            const isProfileUser = user && !user.isFreeAccount;
            navigate(isProfileUser ? '/profile/mailbox' : '/mailbox');
            
        } catch (err) {
            console.error('Error sending documents:', err);
            message.error(err.response?.data?.error || t('documents.view.errorSendingDocuments'));
        }
    };

    const handleUploadDocument = (file, documentFile) => {
        if (documentFile.document_status === 'approved') {
            message.warning(t('documents.view.approvedCannotModify'));
            return false;
        }
        const hasServerFilePath = !!(documentFile.file_path || documentFile.document_path);
        if (documentFile.document_status === 'pending' && hasServerFilePath) {
            message.warning(t('documents.view.pendingCannotModify'));
            return false;
        }
        const documentFileId = documentFile.unique_identifier;
        
        // Validate file type
        if (documentFile.return_type !== '*') {
            const allowedTypes = documentFile.return_type.split(',').map(type => type.trim().toLowerCase());
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const fileType = file.type.toLowerCase();
            
            const isValidExtension = allowedTypes.some(type => 
                type === fileExtension || 
                type === fileType ||
                type.includes(fileExtension)
            );
            
            if (!isValidExtension) {
                message.error(t('documents.view.invalidFileType', { 
                    expected: documentFile.return_type,
                    received: fileExtension 
                }));
                return false;
            }
        }
        
        // Validate file size
        if (systemSettings?.max_upload_size) {
            const maxSizeBytes = parseInt(systemSettings.max_upload_size) * 1024 * 1024; // Convert MB to bytes
            if (file.size > maxSizeBytes) {
                message.error(t('documents.view.fileTooLarge', { 
                    maxSize: systemSettings.max_upload_size 
                }));
                return false;
            }
        }
        
        // If there was a backend file that was removed, remove it from removed files set
        // since we're now uploading a new file for this document
        if (removedFiles.has(documentFileId)) {
            setRemovedFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(documentFileId);
                return newSet;
            });
        }
        
        // Store file locally for later upload
        setUploadedFiles(prev => ({
            ...prev,
            [documentFileId]: {
                file: file, // Store the actual file object
                name: file.name,
                type: file.type,
                size: file.size,
                selectedAt: new Date()
            }
        }));
        
        message.success(t('documents.view.fileSelectedSuccessfully'));
        
        return false; // Prevent default upload behavior
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };


    if (loading) {
        return (
            <div className="document-view-loading">
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="document-view-error-container">
                <Result
                    status="error"
                    title={error}
                    subTitle={t('documents.view.errorSubtitle')}
                    extra={mailboxHref ? [
                        <Button 
                            key="back"
                            type="text"
                            icon={<RollbackOutlined />}
                            onClick={() => navigate(mailboxHref)}
                            className="back-to-mailbox-btn"
                        >
                            {t('documentPreview.backToMailbox')}
                        </Button>
                    ] : []}
                />
            </div>
        );
    }

    return (
        <div className="document-view-container">
            <div className="document-view-top-header">
                {mailboxHref && (
                    <Button
                        type="text"
                        icon={<RollbackOutlined />}
                        onClick={() => navigate(mailboxHref)}
                        className="back-to-mailbox-btn"
                    >
                        {t('documentPreview.backToMailbox')}
                    </Button>
                )}
                <div className="document-view-action-buttons">
                    {viewerEmail && documentData?.sender !== viewerEmail && (
                        <Button 
                            type="primary" 
                            className="action-btn send-documents-btn"
                            icon={<FileOutlined />}
                            onClick={handleSendDocuments}
                        >
                            {t('documents.view.sendDocuments')}
                        </Button>
                    )}
                </div>
            </div>

            {documentData && (
                <>
                    <div className="document-view-detail-body-wrapper">
                    <div 
                        className={`document-view-email-info ${documentData.prevent_message_copy ? 'no-copy' : ''}`}
                        onCopy={(e) => {
                            if (documentData.prevent_message_copy) {
                                e.preventDefault();
                                message.warning('Copying is disabled for this document');
                                return false;
                            }
                        }}
                        onContextMenu={(e) => {
                            if (documentData.prevent_message_copy) {
                                e.preventDefault();
                                return false;
                            }
                        }}
                    >
                        <div className="document-view-icon-wrapper">
                            <MailOutlined />
                        </div>
                        <div className="document-view-details">
                            <div className="document-view-sender-receiver">
                                <div className="document-view-row">
                                    <Text strong>{t('documents.view.from')} </Text>
                                    <Text>{documentData.sender}</Text>
                                </div>
                                <div className="document-view-row">
                                    <Text strong>{t('documents.view.to')} </Text>
                                    <Text>{documentData.recipient}</Text>
                                </div>
                                {documentData.cc && (
                                    <div className="document-view-row">
                                        <Text strong>{t('documents.view.cc')} </Text>
                                        <Text>{documentData.cc}</Text>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Metadata on the right side */}
                        <div className="document-view-metadata-side">
                            {documentData.self_destruct && (
                                <span className="document-view-self-destruct-label">
                                    {t('documents.view.selfDestructLabel')}
                                </span>
                            )}
                            <div className="metadata-item">
                                <div className="metadata-label">{t('documents.view.expires')}</div>
                                <div className="metadata-value">
                                    {dayjs(documentData.ttlive).format('MMM D, YYYY, h:mm A')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subject Line */}
                    <div className="document-view-subject-section">
                        <Title level={3} className="subject-title">
                            {documentData.subject}
                        </Title>
                    </div>

                    {/* Two-column layout: Body + Document Files */}
                    <div 
                        className={`document-view-content-row ${documentData.prevent_message_copy ? 'no-copy' : ''}`} 
                        ref={contentRowRef}
                        onCopy={(e) => {
                            if (documentData.prevent_message_copy) {
                                e.preventDefault();
                                message.warning('Copying is disabled for this document');
                                return false;
                            }
                        }}
                        onContextMenu={(e) => {
                            if (documentData.prevent_message_copy) {
                                e.preventDefault();
                                return false;
                            }
                        }}
                    >
                        {/* Document Body - Left Column */}
                        <div 
                            className="document-view-body-column"
                            style={{ width: `${columnWidths.body}%` }}
                        >
                            {documentData.body && (
                                <div className="document-view-body-section">
                                    <div 
                                        className="document-body-content"
                                        dangerouslySetInnerHTML={{ __html: documentData.body }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Resize Handle */}
                        {sortedDocumentFiles.length > 0 && (
                            <div 
                                className="docview-resize-handle"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIsResizing(true);
                                }}
                            />
                        )}

                        {/* Document Files - Right Column */}
                        {sortedDocumentFiles.length > 0 && (
                            <div 
                                className="document-view-files-column"
                                style={{ width: `${columnWidths.files}%` }}
                            >
                                <div className="docview-files-list-section">
                                    <div className="docview-files-section-header">
                                        <span className="docview-files-section-label">
                                            {t('documents.view.requestedDocumentsLabel')}
                                        </span>
                                        <div className="docview-files-section-header-right">
                                            <span className="docview-files-section-counter">
                                                {(() => {
                                                    const totalCount = sortedDocumentFiles.length;
                                                    const uploadedCount = sortedDocumentFiles.filter(df => {
                                                        const hasServerFile = df.has_been_read && !removedFiles.has(df.unique_identifier);
                                                        const hasLocalFile = !!uploadedFiles[df.unique_identifier];
                                                        return hasServerFile || hasLocalFile;
                                                    }).length;
                                                    const allSentOnLoad = allSentOnLoadRef.current && removedFiles.size === 0 && Object.keys(uploadedFiles).length === 0;
                                                    const showSent = allSentOnLoad && uploadedCount === totalCount;
                                                    return showSent
                                                        ? `${uploadedCount} / ${totalCount} ${t('documents.view.documentsSent')}`
                                                        : `${uploadedCount} / ${totalCount} ${t('documents.view.documentsUploaded')}`;
                                                })()}
                                            </span>
                                            {systemSettings && (
                                                <span className="docview-files-section-max-size">
                                                    {t('documents.view.maxFileSize')}: {systemSettings.max_upload_size ? `${systemSettings.max_upload_size}MB` : t('documents.view.noLimit')}
                                                </span>
                                            )}
                                            {(() => {
                                                const uploadedCount = sortedDocumentFiles.filter(df => {
                                                    const hasServerFile = df.has_been_read && !removedFiles.has(df.unique_identifier);
                                                    const hasLocalFile = !!uploadedFiles[df.unique_identifier];
                                                    return hasServerFile || hasLocalFile;
                                                }).length;
                                                return uploadedCount >= 1;
                                            })() && (
                                                <Tooltip title={t('documentPreview.downloadAll')}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<FolderOpenOutlined />}
                                                        onClick={handleDownloadAll}
                                                        className="docview-files-download-all-btn"
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                    <div className="docview-files-list">
                                        {sortedDocumentFiles.map((documentFile, index) => {
                                            const selectedFile = uploadedFiles[documentFile.unique_identifier];
                                            const hasExistingFile =
                                                !!(documentFile.file_path || documentFile.document_path) &&
                                                !removedFiles.has(documentFile.unique_identifier);
                                            const hasSelectedFile = selectedFile;
                                            const isTreated = documentFile.has_been_treated || (documentFile.file_path && documentFile.file_path !== '');
                                            const isApproved = documentFile.document_status === 'approved';
                                            const uploadLockedPending =
                                                documentFile.document_status === 'pending' && hasExistingFile;
                                            const isUploadRowReadOnly = isApproved || uploadLockedPending;
                                            
                                            const hasUploadedFile = isTreated && documentFile.document_name;
                                            const canPreview = (hasUploadedFile || hasExistingFile) && !removedFiles.has(documentFile.unique_identifier) && isConvertible(documentFile.document_name);
                                            const handleRowClick = (e) => {
                                                if (documentFile.document_status === 'denied') return; // Let Upload handle click for re-upload
                                                if (!canPreview) return;
                                                if (e.target.closest('.docview-file-actions')) return;
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                            };
                                            const tooltipContent = (
                                                <div>
                                                    <div>{t('documentPreview.returnType')}: {documentFile.return_type}</div>
                                                    {hasUploadedFile && documentFile.document_type && (
                                                        <div>{t('documentPreview.documentType')}: {documentFile.document_type}</div>
                                                    )}
                                                    {isTreated && documentFile.document_denied_reason && (
                                                        <div style={{ color: '#ff4d4f' }}>{documentFile.document_denied_reason}</div>
                                                    )}
                                                </div>
                                            );
                                            
                                            const rowContent = (
                                                <>
                                                    <span className="docview-file-title">
                                                        {documentFile.title}
                                                        {(hasSelectedFile || (isTreated && documentFile.document_name)) && (
                                                            <span className="docview-file-uploaded-name">
                                                                : {hasSelectedFile ? selectedFile.name : documentFile.document_name}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <div className="docview-file-status">
                                                        {hasSelectedFile ? (
                                                            <Tag color="blue" className="docview-status-tag-compact">
                                                                {t('documents.view.readyToSend')}
                                                            </Tag>
                                                        ) : isTreated && documentFile.document_name ? (
                                                            <Tag 
                                                                color={
                                                                    documentFile.document_status === 'approved' ? 'green' :
                                                                    documentFile.document_status === 'denied' ? 'red' : 'orange'
                                                                }
                                                                className="docview-status-tag-compact"
                                                            >
                                                                {documentFile.document_status === 'approved' 
                                                                    ? t('documents.view.statusApproved')
                                                                    : documentFile.document_status === 'denied'
                                                                    ? t('documents.view.statusDenied')
                                                                    : t('documents.view.statusPending')}
                                                            </Tag>
                                                        ) : hasExistingFile ? (
                                                            <Tag color="green" className="docview-status-tag-compact">
                                                                {t('documents.view.uploaded')}
                                                            </Tag>
                                                        ) : (
                                                            <Tag color="default" className="docview-status-tag-compact">
                                                                {t('documents.view.statusNotSent')}
                                                            </Tag>
                                                        )}
                                                    </div>
                                                    <div className="docview-file-actions" onClick={(e) => e.stopPropagation()}>
                                                        {canPreview ? (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAttachmentPreviewDoc({
                                                                        documentFileId: documentFile.unique_identifier,
                                                                        fileName: documentFile.document_name
                                                                    });
                                                                }}
                                                                className="docview-file-action-btn docview-file-preview-btn"
                                                            />
                                                        ) : (
                                                            <span className="docview-action-disabled docview-file-action-placeholder">
                                                                <EyeOutlined />
                                                            </span>
                                                        )}
                                                        {!isUploadRowReadOnly && !hasSelectedFile ? (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<UploadOutlined />}
                                                                className="docview-file-action-btn docview-upload-btn-compact"
                                                            />
                                                        ) : (
                                                            <span className="docview-action-disabled docview-file-action-placeholder">
                                                                <UploadOutlined />
                                                            </span>
                                                        )}
                                                        {!isApproved && (hasSelectedFile || hasExistingFile) ? (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<DeleteOutlined />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveFile(documentFile.unique_identifier);
                                                                }}
                                                                className="docview-file-action-btn docview-remove-btn-compact"
                                                            />
                                                        ) : (
                                                            <span className="docview-action-disabled docview-file-action-placeholder">
                                                                <DeleteOutlined />
                                                            </span>
                                                        )}
                                                    </div>
                                                </>
                                            );

                                            return (
                                                <div key={index} className="docview-file-row-wrapper">
                                                    <Tooltip 
                                                        title={tooltipContent}
                                                        placement="top"
                                                    >
                                                        {isUploadRowReadOnly ? (
                                                            <div
                                                                className={`docview-file-row docview-file-row-not-clickable ${canPreview ? 'docview-file-row-previewable' : ''} ${canPreview && (documentFile.comment_count ?? 0) > 0 ? 'docview-file-row-has-comments' : ''}`}
                                                                onClick={handleRowClick}
                                                                role={canPreview ? 'button' : undefined}
                                                                tabIndex={canPreview ? 0 : undefined}
                                                                onKeyDown={canPreview ? (e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleRowClick(e);
                                                                    }
                                                                } : undefined}
                                                            >
                                                                {canPreview && (documentFile.comment_count ?? 0) > 0 && (
                                                                    <span
                                                                        className="docview-file-comment-badge"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                                        }}
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        onKeyDown={(e) => {
                                                                            if ((e.key === 'Enter' || e.key === ' ') && (documentFile.comment_count ?? 0) > 0) {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                                            }
                                                                        }}
                                                                        aria-label={`${documentFile.comment_count} comment${(documentFile.comment_count ?? 0) !== 1 ? 's' : ''}`}
                                                                    >
                                                                        <CommentOutlined />
                                                                        <span className="docview-file-comment-badge-count">{documentFile.comment_count}</span>
                                                                    </span>
                                                                )}
                                                                {rowContent}
                                                            </div>
                                                        ) : (
                                                            <Upload
                                                                accept={documentFile.return_type === '*' ? '*' : documentFile.return_type}
                                                                beforeUpload={(file) => handleUploadDocument(file, documentFile)}
                                                                showUploadList={false}
                                                            >
                                                                <div
                                                                    className={`docview-file-row docview-file-row-clickable ${canPreview ? 'docview-file-row-previewable' : ''} ${canPreview && (documentFile.comment_count ?? 0) > 0 ? 'docview-file-row-has-comments' : ''}`}
                                                                    onClick={handleRowClick}
                                                                    role={canPreview ? 'button' : undefined}
                                                                    tabIndex={canPreview ? 0 : undefined}
                                                                    onKeyDown={canPreview ? (e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleRowClick(e);
                                                                        }
                                                                    } : undefined}
                                                                >
                                                                    {canPreview && (documentFile.comment_count ?? 0) > 0 && (
                                                                        <span
                                                                            className="docview-file-comment-badge"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                                            }}
                                                                            role="button"
                                                                            tabIndex={0}
                                                                            onKeyDown={(e) => {
                                                                                if ((e.key === 'Enter' || e.key === ' ') && (documentFile.comment_count ?? 0) > 0) {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    setAttachmentPreviewDoc({ documentFileId: documentFile.unique_identifier, fileName: documentFile.document_name });
                                                                                }
                                                                            }}
                                                                            aria-label={`${documentFile.comment_count} comment${(documentFile.comment_count ?? 0) !== 1 ? 's' : ''}`}
                                                                        >
                                                                            <CommentOutlined />
                                                                            <span className="docview-file-comment-badge-count">{documentFile.comment_count}</span>
                                                                        </span>
                                                                    )}
                                                                    {rowContent}
                                                                </div>
                                                            </Upload>
                                                        )}
                                                    </Tooltip>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    </div>
                </>
            )}

            <AttachmentPreview
                open={!!attachmentPreviewDoc}
                onClose={() => setAttachmentPreviewDoc(null)}
                documentFileId={attachmentPreviewDoc?.documentFileId}
                fileName={attachmentPreviewDoc?.fileName}
                onDownload={attachmentPreviewDoc ? () => handleDocumentDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName) : undefined}
                source="document"
            />

            <AccessCodeModal
                open={requiresAccessCode}
                rid={unique_identifier}
                onSuccess={handleAccessCodeSuccess}
                onCancel={() => {
                    setRequiresAccessCode(false);
                    setError(t('documents.view.errorAccessCodeRequired') || 'Access code required to view this document');
                }}
            />
        </div>
    );
};

export default DocumentView;
